import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@quorum/api';
import {
  GmAccountInformationQueryParameters,
  GmDealerEligibilityQueryParameters,
  GmMemberLookupQueryParameters,
  GmSoftRegisterEmailQueryParameters,
} from '@quorum/models/oem-query';
import {
  GmAccountInformation,
  GmDealerEligibility,
  GmMemberLookUp,
  GmSoftRegisterEmail,
  LOYMember,
} from '@quorum/models/oem-resource';
import { DealerCodeType, DealerCodeRooftopLink } from '@quorum/models/xs-resource';
import { OemIntegrationService } from '@quorum/quorum-api';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, map, filter, catchError, take } from 'rxjs/operators';
import { GmMyRewardsModule } from './gm-my-rewards.module';
import { AuthenticationStateService } from '@quorum/authentication/services';

@Injectable({
  providedIn: GmMyRewardsModule,
})
export class GmMyRewardsService {
  gmMyRewardsData$: Observable<{
    memberLookup: GmMemberLookUp;
    accountInformation: LOYMember;
    dealerEligibility: GmDealerEligibility;
    businessId: string;
    dealerCountryCode: string;
    emailAddress: string;
  }>;

  constructor(
    private apiService: ApiService,
    private oemIntegrationService: OemIntegrationService,
    private authenticationStateService: AuthenticationStateService
  ) {}

  getMemberLookUpView(
    businessId: string,
    dealerCountryCode: string,
    gmMemberLookupQueryParameters: GmMemberLookupQueryParameters
  ): Observable<GmMemberLookUp> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('quorum-business-id', businessId);
    return this.oemIntegrationService.post(
      `gm-my-rewards/get-member-look-up/specVersion/1.0/countryCode/${dealerCountryCode}/languageCode/en-US`,
      gmMemberLookupQueryParameters,
      {
        headers: headers,
      }
    );
  }

  getAccountInformationView(
    businessId: string,
    dealerCountryCode: string,
    gmAccountInformationQueryParameters: GmAccountInformationQueryParameters
  ): Observable<GmAccountInformation> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('quorum-business-id', businessId);
    return this.oemIntegrationService.post(
      `gm-my-rewards/get-account-information/specVersion/1.0/countryCode/${dealerCountryCode}/languageCode/en-US`,
      gmAccountInformationQueryParameters,
      {
        headers: headers,
      }
    );
  }

  getDealerEligibilityView(
    businessId: string,
    dealerCountryCode: string,
    gmDealerEligibilityQueryParameters: GmDealerEligibilityQueryParameters
  ): Observable<GmDealerEligibility> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('quorum-business-id', businessId);
    return this.oemIntegrationService.post(
      `gm-my-rewards/get-dealer-eligibility/specVersion/1.0/countryCode/${dealerCountryCode}/languageCode/en-US`,
      gmDealerEligibilityQueryParameters,
      {
        headers: headers,
      }
    );
  }

  softRegisterEmail(
    businessId: string,
    dealerCountryCode: string,
    gmSoftRegisterEmailQueryParameters: GmSoftRegisterEmailQueryParameters
  ): Observable<GmSoftRegisterEmail> {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('quorum-business-id', businessId);
    return this.oemIntegrationService.post(
      `gm-my-rewards/get-soft-register-email/specVersion/1.0/countryCode/${dealerCountryCode}/languageCode/en-US`,
      gmSoftRegisterEmailQueryParameters,
      {
        headers: headers,
      }
    );
  }

  getDealerBac() {
    return combineLatest([
      this.apiService
        .get<DealerCodeType[]>(`v/1/system/dealer-code-types`, {
          params: { code: 'bac' },
        })
        .pipe(
          map((dealerCodeTypes: DealerCodeType[]) => dealerCodeTypes[0]?.id),
          filter((id) => !!id),
          catchError(() => of(null))
        ),

      // Get authenticated employee's Rooftop ID
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        map((employee) => employee?.rooftopId),
        filter((rooftopId) => !!rooftopId),
        take(1),
        catchError(() => of(null))
      ),
    ]).pipe(
      filter(([dealerCodeTypeId, rooftopId]) => !!dealerCodeTypeId && !!rooftopId),
      switchMap(([dealerCodeTypeId, rooftopId]) =>
        this.apiService
          .get<DealerCodeRooftopLink[]>(`/v/1/system/dealer-code-rooftop-links`, {
            params: { dealerCodeTypeId, rooftopId },
          })
          .pipe(
            map((links) => links[0]?.dealerCode || ''),
            catchError(() => of(''))
          )
      )
    );
  }
}
